/** ENG-AI-UI-RENDER-1 — the AI-narrative client.
 *
 * A separate module from client.ts on purpose: that file is 510 lines and
 * FILE_SIZE_POLICY calls for splitting past 400 rather than growing.
 *
 * The reason this module exists at all is a field-name mismatch that cost the
 * firm real money. The three narrative endpoints in
 * `apps/api/routers/qoe/deals/narrative.py` do NOT return the session shape:
 *
 *   GET  .../normalization        -> { ai_narrative, narrative_guard, status }
 *   POST .../generate-narrative   -> { narrative,    guard,           status }
 *
 * and in the POST, `status` is the GENERATION outcome ("ok" | "unavailable"),
 * NOT the REPORTGATE-WIRE-1 statement gate ("draft" | "approved"). The page
 * used to merge that response into session state with a blind spread, which
 * (a) left `ai_narrative` null so a freshly generated draft never appeared
 * until a full page reload — and a CPA who clicked "Draft AI narrative" again
 * paid for a second Anthropic call — and (b) overwrote the statement gate with
 * "ok", so generating a narrative made an approved statement read as
 * unapproved.
 *
 * So: nothing here returns the raw wire object. Every function returns a patch
 * typed as narrative fields ONLY, which makes the clobber unrepresentable
 * rather than merely fixed.
 */
import { apiClient, type NarrativeGuard } from '@/api/client'

/** The narrative-owned slice of a normalization session. Deliberately does not
 *  include `status` / `approved_by` / `approved_at` — those belong to the
 *  statement gate and no narrative action may write them. */
export interface NarrativeFields {
  ai_narrative: string | null
  /** The PROSE gate: 'none' | 'draft' | 'approved'. Only 'approved' renders. */
  narrative_status: string
  narrative_guard: NarrativeGuard | null
  narrative_generated_by: string | null
  narrative_approved_by: string | null
  narrative_approved_at: string | null
}

/** Pre-generation state, and what a discard resets to. Mirrors
 *  core/db/qoe_narrative_persistence.clear_narrative(). */
export const EMPTY_NARRATIVE: NarrativeFields = {
  ai_narrative: null,
  narrative_status: 'none',
  narrative_guard: null,
  narrative_generated_by: null,
  narrative_approved_by: null,
  narrative_approved_at: null,
}

/** Anything carrying the GET .../normalization narrative columns. */
export interface NarrativeBearingSession {
  ai_narrative?: string | null
  narrative_status?: string | null
  narrative_guard?: NarrativeGuard | null
  narrative_generated_by?: string | null
  narrative_approved_by?: string | null
  narrative_approved_at?: string | null
}

/** Lift the narrative columns out of a loaded session. Used on load so the
 *  panel reads one state slice whether the draft arrived from a GET or from a
 *  generate — there is no second source of truth to drift. */
export function narrativeFieldsFrom(session: NarrativeBearingSession | null | undefined): NarrativeFields {
  if (!session) return { ...EMPTY_NARRATIVE }
  return {
    ai_narrative: session.ai_narrative ?? null,
    narrative_status: session.narrative_status || 'none',
    narrative_guard: session.narrative_guard ?? null,
    narrative_generated_by: session.narrative_generated_by ?? null,
    narrative_approved_by: session.narrative_approved_by ?? null,
    narrative_approved_at: session.narrative_approved_at ?? null,
  }
}

// ── Role gates ───────────────────────────────────────────────────────────────
//
// Mirrors _DRAFT_ROLES / _RELEASE_ROLES in
// apps/api/routers/qoe/deals/narrative.py. The server is the enforcement point
// — these only decide what the UI offers, so that a role which would be 403'd
// is never handed a button that fails on click. Kept as one exported pair so
// there is a single place to change when the router's tuples change, and so a
// test can assert the two lists still agree.

/** May generate and discard a draft: POST generate-narrative, DELETE narrative. */
export const NARRATIVE_DRAFT_ROLES = ['cpa', 'analyst', 'super_admin'] as const

/** May release a draft into the report: POST approve-narrative. Deliberately
 *  narrower — releasing is what puts AI prose in front of a buyer. */
export const NARRATIVE_RELEASE_ROLES = ['cpa', 'super_admin'] as const

export function canDraftNarrative(role: string | null | undefined): boolean {
  return NARRATIVE_DRAFT_ROLES.includes((role || '') as typeof NARRATIVE_DRAFT_ROLES[number])
}

export function canReleaseNarrative(role: string | null | undefined): boolean {
  return NARRATIVE_RELEASE_ROLES.includes((role || '') as typeof NARRATIVE_RELEASE_ROLES[number])
}

/** Wire shape of POST .../generate-narrative, verified line-by-line against
 *  narrative.py::generate_narrative. Every field optional but `status`: the
 *  "unavailable" branch returns a different subset from the "ok" branch. */
export interface GenerateNarrativeWire {
  session_id?: string
  deal_id?: string
  /** 'ok' | 'unavailable'. NOT the statement gate — see the module docstring. */
  status?: string
  reason?: string | null
  message?: string | null
  /** The prose. Named `narrative` here and `ai_narrative` on the session. */
  narrative?: string | null
  narrative_status?: string | null
  /** The NARRATIVE-GUARD-1 verdict. Named `guard` here, `narrative_guard` on
   *  the session. */
  guard?: NarrativeGuard | null
  narrative_generated_by?: string | null
  model_version?: string | null
  requires_cpa_review?: boolean
}

export type GenerateNarrativeResult =
  /** VALIDATION-1C: a 200 carrying the spend-cap sentinel. Nothing was stored,
   *  so there is no patch to apply and the panel must stay exactly as it was. */
  | { outcome: 'unavailable'; message: string }
  /** A draft was stored. `fields` is safe to merge into session state. */
  | { outcome: 'drafted'; fields: NarrativeFields; guardPassed: boolean }

export const AI_UNAVAILABLE_FALLBACK =
  'AI drafting is unavailable right now. Try again shortly, or write this section yourself.'

/** Translate the generate wire shape into a session patch.
 *
 * Pure and exported so the mapping that broke is the thing under test.
 */
export function mapGeneratedNarrative(wire: GenerateNarrativeWire | null | undefined): GenerateNarrativeResult {
  const text = (wire?.narrative ?? '').trim()
  // Treat anything that is not a stored draft as unavailable. An "ok" with no
  // prose is not a draft, and rendering the empty string would replace the
  // Generate button with a blank panel and no way back.
  if (!wire || wire.status === 'unavailable' || !text) {
    return { outcome: 'unavailable', message: wire?.message || AI_UNAVAILABLE_FALLBACK }
  }
  const guard = wire.guard ?? null
  return {
    outcome: 'drafted',
    fields: {
      ai_narrative: wire.narrative ?? null,
      // Generation never releases; the server always writes "draft". Pinned
      // rather than trusted so a future wire change cannot hand the UI an
      // "approved" the CPA never granted.
      narrative_status: 'draft',
      narrative_guard: guard,
      narrative_generated_by: wire.narrative_generated_by ?? null,
      // save_narrative_draft() clears any prior release on regenerate. Mirror
      // that, or the panel would keep showing "Approved by …" over new prose.
      narrative_approved_by: null,
      narrative_approved_at: null,
    },
    // An absent verdict is not a pass — same rule as GuardResult.from_dict.
    guardPassed: guard?.passed === true,
  }
}

/** POST .../generate-narrative. Bills an Anthropic call — call once per draft. */
export async function generateDealNarrative(dealId: string): Promise<GenerateNarrativeResult> {
  const res = await apiClient.post<GenerateNarrativeWire>(
    `/qoe/deals/${dealId}/normalization/generate-narrative`
  )
  return mapGeneratedNarrative(res.data)
}

/** Wire shape of POST .../approve-narrative. Carries no `status` key, but the
 *  patch is narrowed anyway so it can never grow one. */
export interface ApproveNarrativeWire {
  session_id?: string
  deal_id?: string
  narrative_status?: string | null
  narrative_approved_by?: string | null
  narrative_approved_at?: string | null
}

/** POST .../approve-narrative — the CPA release. Free; no AI call. */
export async function approveDealNarrative(dealId: string): Promise<Partial<NarrativeFields>> {
  const res = await apiClient.post<ApproveNarrativeWire>(
    `/qoe/deals/${dealId}/normalization/approve-narrative`
  )
  return {
    narrative_status: res.data?.narrative_status || 'approved',
    narrative_approved_by: res.data?.narrative_approved_by ?? null,
    narrative_approved_at: res.data?.narrative_approved_at ?? null,
  }
}

/** DELETE .../narrative — the CPA rejecting the prose. Free; no AI call. */
export async function discardDealNarrative(dealId: string): Promise<NarrativeFields> {
  await apiClient.delete(`/qoe/deals/${dealId}/normalization/narrative`)
  return { ...EMPTY_NARRATIVE }
}
